import React, { useState, useEffect, useRef } from "react";
import { IonPage, IonContent, IonButton, IonText, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const MissingNumber: React.FC = () => {
    const [sequence, setSequence] = useState<number[]>([]);
    const [missingNumber, setMissingNumber] = useState<number>(0);
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Missing Number Worksheet for LKG | Number Sequence Activity | Mama’s Guidance";
    }, []);

    const generateNewQuestion = () => {
        const start = Math.floor(Math.random() * 50) + 11; // Start from a random number between 1-10
        //const step = Math.floor(Math.random() * 3) + 1; // Step of 1, 2, or 3
        const step = 1;
        const numbers = Array.from({ length: 5 }, (_, i) => start + i * step);
        const missingIndex = Math.floor(Math.random() * numbers.length);
        const missingValue = numbers[missingIndex];
        numbers[missingIndex] = "?" as any; // Placeholder for missing number

        setSequence(numbers);
        setMissingNumber(missingValue);

        // Generate options
        const wrongOption1 = missingValue + (Math.random() > 0.5 ? 1 : -1);
        const wrongOption2 = missingValue + (Math.random() > 0.5 ? 2 : -2);
        const optionsArray = [missingValue, wrongOption1, wrongOption2].sort(() => Math.random() - 0.5);
        setOptions(optionsArray);
        setMessage("");
    };

    const checkAnswer = (selected: number) => {
        if (selected === missingNumber) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setMessage("❌ Oops! Try again.");
        }
        setTimeout(generateNewQuestion, 1500);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG children strengthen their number sequencing skills with Missing Number activities on Mama’s Guidance—featuring fun and interactive exercises to identify and fill in the missing numbers in a playful learning environment.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord heavyLight matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Missing Number</h1>

                                    <p>
                                        Learning to count is one thing — but recognizing which number is missing in a sequence helps your child understand the correct order of numbers. Our <strong>Missing Number worksheet</strong> is designed to help LKG students practice this important pre-math skill in a playful and effective way.
                                    </p>

                                    <h2>🔍 What Is a Missing Number?</h2>
                                    <p>
                                        In this activity, a sequence of numbers is shown with one or more numbers missing. Children must identify and fill in the blank spaces to complete the correct order. <strong>For example: 4, 5, __, 7 — the missing number is 6</strong>.
                                    </p>

                                    <h2>🎯 What Will Your Child Learn?</h2>
                                    <ul>
                                        <li>Recognizing number patterns and sequences</li>
                                        <li>Counting forward from 1 to 50</li>
                                        <li>Improving <strong>attention and logical thinking</strong></li>
                                        <li>Understanding number placement and progression</li>
                                    </ul>

                                    <h2>🖍️ Worksheet Features</h2>
                                    <ul>
                                        <li>Number sequences from 1 to 50</li>
                                        <li>Simple <strong>“fill in the blank”</strong> tasks</li>
                                        <li>Colorful design and visual cues</li>
                                        <li>Suitable for classroom or home learning</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Teach This Concept</h2>
                                    <ul>
                                        <li>Count out loud with your child from 1 to 50</li>
                                        <li>Write sequences with one number missing and ask them to fill it in</li>
                                        <li>Use number cards and remove one from the row — ask <strong>“What’s missing?”</strong></li>
                                        <li>Give clues like <strong>“What comes after 5 and before 7?”</strong></li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/number-names"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Choose Number Names</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Create a <strong>“number train”</strong> using toy blocks or paper circles. Leave one spot blank and ask your child to find the missing coach! Make learning fun through play.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Missing Number</h1>

                                <p>
                                    Learning to count is one thing — but recognizing which number is missing in a sequence helps your child understand the correct order of numbers. Our <strong>Missing Number worksheet</strong> is designed to help LKG students practice this important pre-math skill in a playful and effective way.
                                </p>

                                <h2>🔍 What Is a Missing Number?</h2>
                                <p>
                                    In this activity, a sequence of numbers is shown with one or more numbers missing. Children must identify and fill in the blank spaces to complete the correct order. <strong>For example: 4, 5, __, 7 — the missing number is 6</strong>.
                                </p>

                                <h2>🎯 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Recognizing number patterns and sequences</li>
                                    <li>Counting forward from 1 to 50</li>
                                    <li>Improving <strong>attention and logical thinking</strong></li>
                                    <li>Understanding number placement and progression</li>
                                </ul>

                                <h2>🖍️ Worksheet Features</h2>
                                <ul>
                                    <li>Number sequences from 1 to 50</li>
                                    <li>Simple <strong>“fill in the blank”</strong> tasks</li>
                                    <li>Colorful design and visual cues</li>
                                    <li>Suitable for classroom or home learning</li>
                                </ul>

                                <h2>👩‍🏫 How to Teach This Concept</h2>
                                <ul>
                                    <li>Count out loud with your child from 1 to 50</li>
                                    <li>Write sequences with one number missing and ask them to fill it in</li>
                                    <li>Use number cards and remove one from the row — ask <strong>“What’s missing?”</strong></li>
                                    <li>Give clues like <strong>“What comes after 5 and before 7?”</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/number-names"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Choose Number Names</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Create a <strong>“number train”</strong> using toy blocks or paper circles. Leave one spot blank and ask your child to find the missing coach! Make learning fun through play.
                                </p>
                            </article>
                            <IonText>
                                <h2>Find the Missing Number</h2>
                            </IonText>
                            <IonText>
                                <h1>{sequence.join(" ")}</h1>
                            </IonText>
                            <div className="mt-4 mb-5" style={{ display: 'flex', gap: '30px' }}>
                                {options.map((option, index) => (
                                    <div className="countingObjectHeading background-blue" style={{ width: 'auto', padding: '15px' }} key={index} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {message && <IonText><h3>{message}</h3></IonText>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MissingNumber;
